import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './SiteHeader.css';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';

const SiteHeader: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userButtonRef = useRef<HTMLButtonElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userProfile = useAppSelector((state) => state.userProfile.profile);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Close user menu on outside click or Escape key
  useEffect(() => {
    const handleDocumentMouseDown = (e: MouseEvent) => {
      if (!userMenuOpen) {
        return;
      }
      const target = e.target as Node;
      if (userButtonRef.current && userButtonRef.current.contains(target)) {
        return;
      }
      if (userMenuRef.current && userMenuRef.current.contains(target)) {
        return;
      }
      setUserMenuOpen(false);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!userMenuOpen) {
        return;
      }
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [userMenuOpen]);

  // Close the user menu automatically on route change
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="site-header">
      <div className="container">
        <nav className="navbar navbar-expand-md navbar-light px-0">
          <div className="d-flex align-items-center gap-2">
            <img
              src="/LogoNoTitle-Small.png"
              alt="Walk It With Me logo"
              className="site-header-logo"
            />
            <Link to="/" className="text-decoration-none">
              <h1 className="site-header-title navbar-brand mb-0 pe-3 pe-md-4">
                Walk It With Me <sup className="text-danger">Beta</sup>
              </h1>
            </Link>
          </div>

          {isAuthenticated && (
            <button
              className="navbar-toggler ms-auto"
              type="button"
              aria-controls="siteHeaderNav"
              aria-label="Toggle navigation"
              onClick={() => setNavOpen((v) => !v)}
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          )}

          {!isAuthenticated && location.pathname !== '/' && (
            <div className="ms-auto d-flex align-items-center gap-3">
              <Link to="/about" className="nav-link site-nav-link">
                Learn More
              </Link>
              <Link to="/login#signup" className="btn btn-primary btn-sm">
                Sign Up
              </Link>
            </div>
          )}

          {isAuthenticated && (
            <div id="siteHeaderNav" className={`collapse navbar-collapse ${navOpen ? 'show' : ''}`}>
              {/* Left side nav */}
              <ul className="navbar-nav me-auto gap-2 gap-md-3">
                <li className="nav-item">
                  <NavLink
                    to="/chat"
                    className={({ isActive }) =>
                      `nav-link d-flex align-items-center gap-2 site-nav-link${isActive ? ' active' : ''}`
                    }
                    onClick={() => setNavOpen(false)}
                  >
                    <i className="bi bi-chat-left-text" aria-hidden="true"></i>
                    <span>Chat</span>
                  </NavLink>
                </li>
                {/* <li className="nav-item">
                  <NavLink
                    to="/study"
                    className={({ isActive }) =>
                      `nav-link d-flex align-items-center gap-2 site-nav-link${isActive ? ' active' : ''}`
                    }
                    onClick={() => setNavOpen(false)}
                  >
                    <i className="bi bi-book" aria-hidden="true"></i>
                    <span>Study</span>
                  </NavLink>
                </li> */}
                <li className="nav-item">
                  <NavLink
                    to="/devotional"
                    className={({ isActive }) =>
                      `nav-link d-flex align-items-center gap-2 site-nav-link${isActive ? ' active' : ''}`
                    }
                    onClick={() => setNavOpen(false)}
                  >
                    <i className="bi bi-calendar-heart" aria-hidden="true"></i>
                    <span>Devotional</span>
                  </NavLink>
                </li>
              </ul>

              {/* Right side nav (account menu) */}
              <ul className="navbar-nav ms-auto align-items-md-center">
                <li className="nav-item dropdown">
                  <button
                    className="btn btn-link nav-link d-flex align-items-center gap-2 site-nav-link"
                    id="userMenuButton"
                    aria-haspopup="true"
                    aria-label="User menu"
                    ref={userButtonRef}
                    onClick={() => setUserMenuOpen((open) => !open)}
                  >
                    <i className="bi bi-person-circle" aria-hidden="true"></i>
                    <span className="d-inline">{userProfile?.properties['name'] || 'Account'}</span>
                  </button>
                  {userMenuOpen && (
                    <div
                      className="dropdown-menu dropdown-menu-end show"
                      aria-labelledby="userMenuButton"
                      role="menu"
                      ref={userMenuRef}
                    >
                      <button
                        className="dropdown-item"
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          setNavOpen(false);
                          navigate('/profile');
                        }}
                      >
                        <i className="bi bi-person-lines-fill text-primary" aria-hidden="true"></i>
                        <span className="d-inline"> Profile</span>
                      </button>
                      <div className="dropdown-divider" />
                      <button
                        className="dropdown-item"
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          setNavOpen(false);
                          navigate('/favorites');
                        }}
                      >
                        <i className="bi bi-heart-fill text-danger" aria-hidden="true"></i>
                        <span className="d-inline"> My Favorites</span>
                      </button>
                      <div className="dropdown-divider" />
                      <button
                        className="dropdown-item"
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          setNavOpen(false);
                          navigate('/about');
                        }}
                      >
                        <i className="bi bi-info-circle text-info" aria-hidden="true"></i>
                        <span className="d-inline"> About</span>
                      </button>
                      <div className="dropdown-divider" />
                      <button
                        className="dropdown-item"
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          setNavOpen(false);
                          handleLogout();
                        }}
                      >
                        <i className="bi bi-box-arrow-right text-danger" aria-hidden="true"></i>
                        <span className="d-inline"> Logout</span>
                      </button>
                      <div className="dropdown-divider" />
                      <div className="dropdown-item-text text-muted small">
                        Version: {process.env.REACT_APP_VERSION || 'dev'}
                      </div>
                    </div>
                  )}
                </li>
              </ul>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default SiteHeader;
